import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { RefreshCw } from 'lucide-react'
import { SyncCard } from '../-components/SyncCard'
import { BulkSyncClientsPopover } from './-components/BulkSyncClientsPopover'
import { SyncModeDialog } from './-components/SyncModeDialog'
import { EmailAttachmentSyncCard } from './-components/EmailAttachmentSyncCard'
import { WebhookSubscriptionsList } from './-components/WebhookSubscriptionsList'
import { SyncHistoryFilters } from './-components/SyncHistoryFilters'
import { SyncHistoryTable } from './-components/SyncHistoryTable'
import { SyncBatchDetailSheet } from './-components/SyncBatchDetailSheet'
import { WebhookEventFilters } from './-components/WebhookEventFilters'
import { WebhookEventsScopeTabs } from './-components/WebhookEventsScopeTabs'
import { WebhookEventsTable } from './-components/WebhookEventsTable'
import { WebhookEventDetailSheet } from './-components/WebhookEventDetailSheet'
import { RecordsFilters } from './-components/RecordsFilters'
import { ExperienceRollups } from './-components/ExperienceRollups'
import { InvoiceIssueRollups } from './-components/InvoiceIssueRollups'
import { MeetingIssueRollups } from './-components/MeetingIssueRollups'
import { QuoteIssueRollups } from './-components/QuoteIssueRollups'
import { LineItemRollups } from './-components/LineItemRollups'
import { MissingClientCheck } from './-components/MissingClientCheck'
import { InvoiceIssuesFixPanel } from './-components/InvoiceIssuesFixPanel'
import { MeetingTypeMergeDialog } from './-components/MeetingTypeMergeDialog'
import { RecordsTable } from './-components/RecordsTable'
import type {
  HubspotInvoiceIssueType,
  HubspotRecordType,
  HubspotRecordsFilters,
  LatestSyncStatus,
  SyncBatchStatus,
  SyncBatchesFilters,
  WebhookEventsFilters,
} from '@/types/hubspot.types'

import type { HubspotSyncMode } from '@/hooks/repositories/useHubSpotRepository'
import { PageTitle } from '@/components/PageTitle'
import { HubSpotIcon } from '@/components/ExternalLinks'
import { Button } from '@/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  useClientSyncStatus,
  useDealsSyncStatus,
  useEmailAttachmentSyncStatus,
  useHubSpotRecords,
  useHubSpotSyncBatches,
  useHubSpotWebhookEvents,
  useInvoiceSyncStatus,
  useMeetingsSyncStatus,
  useQuotesSyncStatus,
  useSyncAllClientsHubSpot,
  useSyncAllUsersHubSpot,
  useSyncHubspotObject,
  useTicketsSyncStatus,
  useUserSyncStatus,
} from '@/hooks/repositories/useHubSpotRepository'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

const tabSchema = z.object({
  tab: z.enum(['sync', 'webhooks', 'records']).optional().default('sync'),
  clientId: z.string().uuid().optional(),
  recordType: z
    .enum([
      'experiences',
      'products',
      'invoices',
      'line_items',
      'payments',
      'associations',
      'deals',
      'tickets',
      'quotes',
      'meetings',
    ])
    .optional(),
  invoiceIssue: z
    .enum([
      'missing_owner',
      'missing_deal',
      'missing_experience',
      'overdue',
      'needs_review',
      'no_contact',
    ])
    .optional()
    .catch(undefined),
})

type HubSpotRouteSearch = z.infer<typeof tabSchema>

export const Route = createFileRoute('/_app/backoffice/integrations/hubspot/')({
  component: RouteComponent,
  validateSearch: tabSchema,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.hubspot),
})

const PER_PAGE = 25

/**
 * Derive a SyncBatchStatus-compatible object from a LatestSyncStatus for use
 * as the card's batchStatus while the batch is still in-flight.
 * Status logic:
 *   - completedAt set → 'completed'
 *   - totalRecords > 0 → 'processing' (worker has started counting)
 *   - otherwise → 'queued' (batch created but worker hasn't started yet)
 */
function deriveSync(
  status: LatestSyncStatus | null | undefined,
  isPending: boolean,
): { isActive: boolean; batchStatus: SyncBatchStatus | null } {
  const isActive = (!!status && !status.completedAt) || isPending
  return {
    isActive,
    batchStatus: isActive && status ? latestStatusToBatchStatus(status) : null,
  }
}

function latestStatusToBatchStatus(status: LatestSyncStatus): SyncBatchStatus {
  const processed = status.successCount + status.failureCount
  const derivedStatus = status.completedAt
    ? 'completed'
    : status.totalRecords > 0
      ? 'processing'
      : 'queued'
  const progressPercentage = status.completedAt
    ? 100
    : status.totalRecords > 0
      ? Math.min(99, Math.round((processed / status.totalRecords) * 100))
      : 0
  return {
    batchId: status.id,
    batchType: status.batchType,
    status: derivedStatus,
    totalRecords: status.totalRecords,
    successCount: status.successCount,
    failureCount: status.failureCount,
    processingCount: 0,
    progressPercentage,
    startedAt: status.startedAt,
    completedAt: status.completedAt,
    initiatedBy: status.initiatedUser,
    errorDetails: status.errorDetails,
  }
}

function SyncCards() {
  const syncAllUsers = useSyncAllUsersHubSpot()
  const { data: userSyncStatus, isLoading: isLoadingUserStatus } =
    useUserSyncStatus()

  const syncAllClients = useSyncAllClientsHubSpot()
  const { data: clientSyncStatus, isLoading: isLoadingClientStatus } =
    useClientSyncStatus()

  const syncInvoice = useSyncHubspotObject('invoice')
  const { data: invoiceSyncStatus, isLoading: isLoadingInvoiceStatus } =
    useInvoiceSyncStatus()

  const syncDeal = useSyncHubspotObject('deal')
  const { data: dealsSyncStatus, isLoading: isLoadingDealsStatus } =
    useDealsSyncStatus()

  const syncTicket = useSyncHubspotObject('ticket')
  const { data: ticketsSyncStatus, isLoading: isLoadingTicketsStatus } =
    useTicketsSyncStatus()

  const syncQuote = useSyncHubspotObject('quote')
  const { data: quotesSyncStatus, isLoading: isLoadingQuotesStatus } =
    useQuotesSyncStatus()

  const syncMeeting = useSyncHubspotObject('meeting')
  const { data: meetingsSyncStatus, isLoading: isLoadingMeetingsStatus } =
    useMeetingsSyncStatus()

  const {
    data: emailAttachmentSyncStatus,
    isLoading: isLoadingEmailAttachmentStatus,
  } = useEmailAttachmentSyncStatus()

  const { isActive: isUserSyncActive, batchStatus: userBatchStatus } =
    deriveSync(userSyncStatus, syncAllUsers.isPending)
  const { isActive: isClientSyncActive, batchStatus: clientBatchStatus } =
    deriveSync(clientSyncStatus, syncAllClients.isPending)
  const { isActive: isInvoiceSyncActive, batchStatus: invoiceBatchStatus } =
    deriveSync(invoiceSyncStatus, syncInvoice.isPending)
  const { isActive: isDealsSyncActive, batchStatus: dealsBatchStatus } =
    deriveSync(dealsSyncStatus, syncDeal.isPending)
  const { isActive: isTicketsSyncActive, batchStatus: ticketsBatchStatus } =
    deriveSync(ticketsSyncStatus, syncTicket.isPending)
  const { isActive: isQuotesSyncActive, batchStatus: quotesBatchStatus } =
    deriveSync(quotesSyncStatus, syncQuote.isPending)
  const { isActive: isMeetingsSyncActive, batchStatus: meetingsBatchStatus } =
    deriveSync(meetingsSyncStatus, syncMeeting.isPending)

  const handleUserSync = () => {
    syncAllUsers.mutate(undefined)
  }

  const handleClientSync = (payload?: {
    pull?: Array<string>
    push?: Array<string>
  }) => {
    syncAllClients.mutate(payload as never)
  }

  const handleInvoiceSync = (mode: HubspotSyncMode) => {
    syncInvoice.mutate(mode)
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <SyncCard
        title="HubSpot Users"
        description="Matches Rothology team members to their HubSpot owner and user records by email, then stores those IDs locally so advisor, planner, and specialist syncs can resolve cleanly later. This sync updates local mappings only."
        entityName="users"
        onSync={handleUserSync}
        isSyncing={isUserSyncActive}
        batchStatus={userBatchStatus ?? null}
        isBatchActive={isUserSyncActive}
        lastSyncStatus={userSyncStatus ?? null}
        isLoadingStatus={isLoadingUserStatus}
      />
      <SyncCard
        title="HubSpot Clients"
        description="For each Rothology client, refresh the linked HubSpot contact and local association record, optionally pull advisor, planner, and specialist assignments from HubSpot, and push Rothology IDs and links back to the contact. This bulk sync does not import deals or tickets."
        entityName="clients"
        onSync={() => handleClientSync()}
        isSyncing={isClientSyncActive}
        batchStatus={clientBatchStatus ?? null}
        isBatchActive={isClientSyncActive}
        lastSyncStatus={clientSyncStatus ?? null}
        isLoadingStatus={isLoadingClientStatus}
        actionSlot={
          <BulkSyncClientsPopover
            isSyncing={isClientSyncActive}
            onStart={handleClientSync}
            trigger={
              <Button
                size="sm"
                variant="outline"
                disabled={isClientSyncActive}
                className="h-8 gap-1.5 text-xs"
              >
                <RefreshCw
                  className={
                    isClientSyncActive ? 'size-3.5 animate-spin' : 'size-3.5'
                  }
                />
                {isClientSyncActive ? 'Syncing...' : 'Sync Now'}
              </Button>
            }
          />
        }
      />
      <SyncCard
        title="HubSpot Invoices & Payments"
        description="Crawls every HubSpot invoice and its payments, upserts the local billing tables, and safely replays paid-invoice onboarding only when the local record is stale. Re-runs skip invoices and payments that are already current."
        entityName="invoices & payments"
        onSync={() => handleInvoiceSync('full')}
        isSyncing={isInvoiceSyncActive}
        batchStatus={invoiceBatchStatus ?? null}
        isBatchActive={isInvoiceSyncActive}
        lastSyncStatus={invoiceSyncStatus ?? null}
        isLoadingStatus={isLoadingInvoiceStatus}
        actionSlot={
          <SyncModeDialog
            objectLabel="Invoices & Payments"
            isSyncing={isInvoiceSyncActive}
            onConfirm={handleInvoiceSync}
            isPending={syncInvoice.isPending}
          />
        }
      />
      <SyncCard
        title="HubSpot Meetings"
        description="Pulls HubSpot meetings and links them to clients. Runs hourly (incremental); reconciliation runs daily."
        entityName="meetings"
        onSync={() => syncMeeting.mutate('full')}
        isSyncing={isMeetingsSyncActive}
        batchStatus={meetingsBatchStatus}
        isBatchActive={isMeetingsSyncActive}
        lastSyncStatus={meetingsSyncStatus ?? null}
        isLoadingStatus={isLoadingMeetingsStatus}
        actionSlot={
          <SyncModeDialog
            objectLabel="Meetings"
            isSyncing={isMeetingsSyncActive}
            onConfirm={(mode) => syncMeeting.mutate(mode)}
            isPending={syncMeeting.isPending}
          />
        }
      />
      <SyncCard
        title="HubSpot Deals"
        description="Crawls every client's HubSpot deals, upserts the mirror + associations classified by pipeline, and archives stale links. Webhooks keep them live between runs."
        entityName="contacts"
        onSync={() => syncDeal.mutate('full')}
        isSyncing={isDealsSyncActive}
        batchStatus={dealsBatchStatus}
        isBatchActive={isDealsSyncActive}
        lastSyncStatus={dealsSyncStatus ?? null}
        isLoadingStatus={isLoadingDealsStatus}
        actionSlot={
          <SyncModeDialog
            objectLabel="Deals"
            isSyncing={isDealsSyncActive}
            onConfirm={(mode) => syncDeal.mutate(mode)}
            isPending={syncDeal.isPending}
          />
        }
      />
      <SyncCard
        title="HubSpot Tickets"
        description="Crawls every client's HubSpot tickets, upserts the mirror + associations classified by pipeline, and archives stale links. Webhooks keep them live between runs."
        entityName="contacts"
        onSync={() => syncTicket.mutate('full')}
        isSyncing={isTicketsSyncActive}
        batchStatus={ticketsBatchStatus}
        isBatchActive={isTicketsSyncActive}
        lastSyncStatus={ticketsSyncStatus ?? null}
        isLoadingStatus={isLoadingTicketsStatus}
        actionSlot={
          <SyncModeDialog
            objectLabel="Tickets"
            isSyncing={isTicketsSyncActive}
            onConfirm={(mode) => syncTicket.mutate(mode)}
            isPending={syncTicket.isPending}
          />
        }
      />
      <SyncCard
        title="HubSpot Quotes"
        description="Crawls every client's HubSpot quotes, upserts the mirror + associations, and archives stale links. Webhooks keep them live between runs."
        entityName="contacts"
        onSync={() => syncQuote.mutate('full')}
        isSyncing={isQuotesSyncActive}
        batchStatus={quotesBatchStatus}
        isBatchActive={isQuotesSyncActive}
        lastSyncStatus={quotesSyncStatus ?? null}
        isLoadingStatus={isLoadingQuotesStatus}
        actionSlot={
          <SyncModeDialog
            objectLabel="Quotes"
            isSyncing={isQuotesSyncActive}
            onConfirm={(mode) => syncQuote.mutate(mode)}
            isPending={syncQuote.isPending}
          />
        }
      />
      <EmailAttachmentSyncCard
        status={emailAttachmentSyncStatus}
        isLoadingStatus={isLoadingEmailAttachmentStatus}
      />
    </div>
  )
}

function SyncHistorySection({ initialClientId }: { initialClientId?: string }) {
  const [filters, setFilters] = React.useState<SyncBatchesFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
  })
  const [selectedBatchId, setSelectedBatchId] = React.useState<string | null>(
    null,
  )
  const [sheetOpen, setSheetOpen] = React.useState(false)

  const { data, isLoading } = useHubSpotSyncBatches(filters)

  const handleRowClick = (batchId: string) => {
    setSelectedBatchId(batchId)
    setSheetOpen(true)
  }

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Sync History</CardTitle>
        <CardDescription className="text-xs">
          Every bulk sync operation between Rothology and HubSpot.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <SyncHistoryFilters filters={filters} onFiltersChange={setFilters} />
        <SyncHistoryTable
          data={data?.items ?? []}
          isLoading={isLoading}
          pagination={{
            currentPage: data?.page ?? 1,
            totalPages: data?.totalPages ?? 1,
            total: data?.total ?? 0,
            onPageChange: (page) => setFilters((f) => ({ ...f, page })),
          }}
          onRowClick={handleRowClick}
        />
        <SyncBatchDetailSheet
          batchId={selectedBatchId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
        />
      </CardContent>
    </Card>
  )
}

function WebhookEventsSection({
  initialClientId,
}: {
  initialClientId?: string
}) {
  const [filters, setFilters] = React.useState<WebhookEventsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
    scope: 'rothology',
  })
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(
    null,
  )
  const [sheetOpen, setSheetOpen] = React.useState(false)

  const { data, isLoading } = useHubSpotWebhookEvents(filters)

  const scopeCounts = data?.scopeCounts ?? {
    all: 0,
    rothology: 0,
    untracked: 0,
  }

  const handleRowClick = (eventId: string) => {
    setSelectedEventId(eventId)
    setSheetOpen(true)
  }

  const handleScopeChange = (scope: 'all' | 'rothology' | 'untracked') => {
    setFilters((f) => ({ ...f, scope, page: 1 }))
  }

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Webhook Events</CardTitle>
        <CardDescription className="text-xs">
          Every webhook HubSpot has delivered. Rothology — events tied to a
          tracked client. Non-tracked — events for contacts / deals / tickets in
          the HubSpot portal that aren't onboarded here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <WebhookEventsScopeTabs
          scope={filters.scope ?? 'rothology'}
          counts={scopeCounts}
          onChange={handleScopeChange}
        />
        <WebhookEventFilters filters={filters} onFiltersChange={setFilters} />
        <WebhookEventsTable
          data={data?.items ?? []}
          isLoading={isLoading}
          pagination={{
            currentPage: data?.page ?? 1,
            totalPages: data?.totalPages ?? 1,
            total: data?.total ?? 0,
            onPageChange: (page) => setFilters((f) => ({ ...f, page })),
          }}
          onRowClick={handleRowClick}
        />
        <WebhookEventDetailSheet
          eventId={selectedEventId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
        />
      </CardContent>
    </Card>
  )
}

function SyncTab({ initialClientId }: { initialClientId?: string }) {
  return (
    <div className="space-y-4">
      <SyncCards />
      <SyncHistorySection initialClientId={initialClientId} />
    </div>
  )
}

function WebhooksTab({ initialClientId }: { initialClientId?: string }) {
  return (
    <div className="space-y-4">
      <WebhookSubscriptionsList />
      <WebhookEventsSection initialClientId={initialClientId} />
    </div>
  )
}

function RecordsSection({
  initialClientId,
  initialRecordType,
  initialInvoiceIssue,
  onRecordTypeChange,
}: {
  initialClientId?: string
  initialRecordType: HubspotRecordType
  initialInvoiceIssue?: HubspotInvoiceIssueType
  onRecordTypeChange: (recordType: HubspotRecordType) => void
}) {
  const [filters, setFilters] = React.useState<HubspotRecordsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
    recordType: initialRecordType,
    invoiceIssue:
      initialRecordType === 'invoices' ? initialInvoiceIssue : undefined,
  })

  React.useEffect(() => {
    setFilters((current) => {
      if (current.recordType === initialRecordType) {
        return current
      }

      return {
        page: 1,
        perPage: current.perPage,
        clientId: current.clientId,
        recordType: initialRecordType,
      }
    })
  }, [initialRecordType])

  const { data, isLoading } = useHubSpotRecords(filters)

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Records</CardTitle>
        <CardDescription className="text-xs">
          Search derived experiences plus joined commerce and HubSpot-linked
          records across products, invoices, line items, payments, and client
          associations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RecordsFilters
          filters={filters}
          onFiltersChange={setFilters}
          onRecordTypeChange={onRecordTypeChange}
          meetingTypes={data?.summary?.meetingTypes}
        />
        {filters.recordType === 'experiences' && data?.summary?.experiences && (
          <ExperienceRollups summaries={data.summary.experiences} />
        )}
        {filters.recordType === 'invoices' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {data?.summary?.invoiceIssues && (
                <InvoiceIssueRollups
                  summaries={data.summary.invoiceIssues}
                  activeIssue={filters.invoiceIssue}
                  onIssueChange={(invoiceIssue) =>
                    setFilters((current) => ({
                      ...current,
                      invoiceIssue,
                      page: 1,
                    }))
                  }
                />
              )}
              {data?.summary?.missingClientCount != null &&
                data.summary.missingClientCount > 0 && (
                  <MissingClientCheck
                    count={data.summary.missingClientCount}
                    isActive={filters.missingClient === true}
                    onToggle={() =>
                      setFilters((current) => ({
                        ...current,
                        missingClient:
                          current.missingClient === true ? undefined : true,
                        page: 1,
                      }))
                    }
                  />
                )}
              <InvoiceIssuesFixPanel
                issueSummaries={data?.summary?.invoiceIssues}
              />
            </div>
          </div>
        )}
        {filters.recordType === 'line_items' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {data?.summary?.lineItems && (
                <LineItemRollups
                  summaries={data.summary.lineItems}
                  activeFacet={filters.lineItemFacet}
                  onFacetChange={(lineItemFacet) =>
                    setFilters((current) => ({
                      ...current,
                      lineItemFacet,
                      page: 1,
                    }))
                  }
                />
              )}
              {data?.summary?.missingClientCount != null &&
                data.summary.missingClientCount > 0 && (
                  <MissingClientCheck
                    count={data.summary.missingClientCount}
                    isActive={filters.missingClient === true}
                    onToggle={() =>
                      setFilters((current) => ({
                        ...current,
                        missingClient:
                          current.missingClient === true ? undefined : true,
                        page: 1,
                      }))
                    }
                  />
                )}
            </div>
          </div>
        )}
        {filters.recordType === 'payments' &&
          data?.summary?.missingClientCount != null &&
          data.summary.missingClientCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <MissingClientCheck
                count={data.summary.missingClientCount}
                isActive={filters.missingClient === true}
                onToggle={() =>
                  setFilters((current) => ({
                    ...current,
                    missingClient:
                      current.missingClient === true ? undefined : true,
                    page: 1,
                  }))
                }
              />
            </div>
          )}
        {filters.recordType === 'deals' &&
          data?.summary?.missingClientCount != null &&
          data.summary.missingClientCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <MissingClientCheck
                count={data.summary.missingClientCount}
                isActive={filters.missingClient === true}
                onToggle={() =>
                  setFilters((current) => ({
                    ...current,
                    missingClient:
                      current.missingClient === true ? undefined : true,
                    page: 1,
                  }))
                }
              />
            </div>
          )}
        {filters.recordType === 'tickets' &&
          data?.summary?.missingClientCount != null &&
          data.summary.missingClientCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <MissingClientCheck
                count={data.summary.missingClientCount}
                isActive={filters.missingClient === true}
                onToggle={() =>
                  setFilters((current) => ({
                    ...current,
                    missingClient:
                      current.missingClient === true ? undefined : true,
                    page: 1,
                  }))
                }
              />
            </div>
          )}
        {filters.recordType === 'quotes' &&
          ((data?.summary?.quoteIssues?.length ?? 0) > 0 ||
            (data?.summary?.missingClientCount ?? 0) > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {data?.summary?.quoteIssues && (
                <QuoteIssueRollups
                  summaries={data.summary.quoteIssues}
                  activeIssue={filters.quoteIssue}
                  onIssueChange={(quoteIssue) =>
                    setFilters((current) => ({
                      ...current,
                      quoteIssue,
                      page: 1,
                    }))
                  }
                />
              )}
              {data?.summary?.missingClientCount != null &&
                data.summary.missingClientCount > 0 && (
                  <MissingClientCheck
                    count={data.summary.missingClientCount}
                    isActive={filters.missingClient === true}
                    onToggle={() =>
                      setFilters((current) => ({
                        ...current,
                        missingClient:
                          current.missingClient === true ? undefined : true,
                        page: 1,
                      }))
                    }
                  />
                )}
            </div>
          )}
        {filters.recordType === 'meetings' && (
          <div className="space-y-3">
            {data?.summary?.meetingScanCapped && (
              <p className="text-xs text-muted-foreground">
                Issue checks cover the most recently modified meetings.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {data?.summary?.meetingIssues && (
                <MeetingIssueRollups
                  summaries={data.summary.meetingIssues}
                  activeIssue={filters.meetingIssue}
                  onIssueChange={(meetingIssue) =>
                    setFilters((current) => ({
                      ...current,
                      meetingIssue,
                      page: 1,
                    }))
                  }
                />
              )}
              {data?.summary?.missingClientCount != null &&
                data.summary.missingClientCount > 0 && (
                  <MissingClientCheck
                    count={data.summary.missingClientCount}
                    isActive={filters.missingClient === true}
                    onToggle={() =>
                      setFilters((current) => ({
                        ...current,
                        missingClient:
                          current.missingClient === true ? undefined : true,
                        page: 1,
                      }))
                    }
                  />
                )}
              {(data?.summary?.meetingTypes?.length ?? 0) > 1 && (
                <MeetingTypeMergeDialog
                  meetingTypes={data?.summary?.meetingTypes ?? []}
                />
              )}
            </div>
          </div>
        )}
        <RecordsTable
          data={data?.items ?? []}
          filters={filters}
          isLoading={isLoading}
          pagination={{
            currentPage: data?.page ?? 1,
            totalPages: data?.totalPages ?? 1,
            total: data?.total ?? 0,
            onPageChange: (page) =>
              setFilters((current) => ({ ...current, page })),
          }}
        />
      </CardContent>
    </Card>
  )
}

function RecordsTab({
  initialClientId,
  initialRecordType,
  initialInvoiceIssue,
  onRecordTypeChange,
}: {
  initialClientId?: string
  initialRecordType: HubspotRecordType
  initialInvoiceIssue?: HubspotInvoiceIssueType
  onRecordTypeChange: (recordType: HubspotRecordType) => void
}) {
  return (
    <div className="space-y-4">
      <RecordsSection
        initialClientId={initialClientId}
        initialRecordType={initialRecordType}
        initialInvoiceIssue={initialInvoiceIssue}
        onRecordTypeChange={onRecordTypeChange}
      />
    </div>
  )
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { tab, clientId, recordType, invoiceIssue } = Route.useSearch()

  const handleTabChange = (value: string) => {
    const nextTab = value as 'sync' | 'webhooks' | 'records'
    void navigate({
      search: (prev: HubSpotRouteSearch) => {
        const { recordType: prevRecordType, ...rest } = prev
        return {
          ...rest,
          tab: nextTab,
          ...(nextTab === 'records' && prevRecordType
            ? { recordType: prevRecordType }
            : {}),
        }
      },
      replace: true,
    })
  }

  const handleRecordTypeChange = (nextRecordType: HubspotRecordType) => {
    void navigate({
      search: (prev: HubSpotRouteSearch) => ({
        ...prev,
        recordType: nextRecordType,
      }),
      replace: true,
    })
  }

  return (
    <div className="py-4">
      <PageTitle title="HubSpot Integration" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>HubSpot</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-6 flex items-center gap-3">
        <HubSpotIcon className="size-7 shrink-0" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            HubSpot Integration
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Manage sync operations, monitor webhook events, and audit synced
            records.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="sync">Sync</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="records">Records</TabsTrigger>
        </TabsList>
        <TabsContent value="sync" className="mt-4 w-full">
          <SyncTab initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4 w-full">
          <WebhooksTab initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="records" className="mt-4 w-full">
          <RecordsTab
            initialClientId={clientId}
            initialRecordType={recordType ?? 'experiences'}
            initialInvoiceIssue={invoiceIssue}
            onRecordTypeChange={handleRecordTypeChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
